import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../services/api'
import { PageHeader, Card, Button, Field, Input } from '../components/ui'

interface LoyaltyReward {
  id: string
  label: string
  pointsCost: number
  discountValue: number
  active: boolean
}

interface LoyaltyAccount {
  id: string
  phone: string
  points: number
  createdAt: string
  _count: { transactions: number }
}

const EMPTY_REWARD = { label: '', pointsCost: '', discountValue: '', active: true }

export default function LoyaltyPage() {
  return (
    <div className="p-8 max-w-4xl mx-auto space-y-8">
      <PageHeader
        title="Programa de Lealtad"
        subtitle="Configura las recompensas y consulta las cuentas de tus clientes"
      />
      <RewardsSection />
      <AccountsSection />
    </div>
  )
}

// ─── Recompensas ──────────────────────────────────────────────────────────────
function RewardsSection() {
  const qc = useQueryClient()
  const [form, setForm] = useState(EMPTY_REWARD)
  const [editId, setEditId] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)

  const { data: rewards = [], isLoading } = useQuery<LoyaltyReward[]>({
    queryKey: ['loyalty-rewards'],
    queryFn: async () => (await api.get('/loyalty/rewards')).data,
  })

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        label: form.label,
        pointsCost: Number(form.pointsCost),
        discountValue: Number(form.discountValue),
        active: form.active,
      }
      if (editId) return (await api.patch(`/loyalty/rewards/${editId}`, payload)).data
      return (await api.post('/loyalty/rewards', payload)).data
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['loyalty-rewards'] })
      setForm(EMPTY_REWARD)
      setEditId(null)
      setShowForm(false)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/loyalty/rewards/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['loyalty-rewards'] }),
  })

  const toggleMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.patch(`/loyalty/rewards/${id}`, { active }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['loyalty-rewards'] }),
  })

  function openEdit(r: LoyaltyReward) {
    setForm({
      label: r.label,
      pointsCost: String(r.pointsCost),
      discountValue: String(r.discountValue),
      active: r.active,
    })
    setEditId(r.id)
    setShowForm(true)
  }

  function cancelForm() {
    setForm(EMPTY_REWARD)
    setEditId(null)
    setShowForm(false)
  }

  return (
    <Card>
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <h2 className="font-bold text-gray-900">Recompensas</h2>
        {!showForm && (
          <Button onClick={() => setShowForm(true)}>+ Nueva recompensa</Button>
        )}
      </div>

      {showForm && (
        <div className="p-6 border-b border-gray-100 space-y-4 bg-gray-50">
          <h3 className="text-sm font-semibold text-gray-700">
            {editId ? 'Editar recompensa' : 'Nueva recompensa'}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <Field label="Nombre">
              <Input
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                placeholder="Ej. Platillo gratis"
              />
            </Field>
            <Field label="Puntos requeridos">
              <Input
                type="number"
                value={form.pointsCost}
                onChange={(e) => setForm((f) => ({ ...f, pointsCost: e.target.value }))}
                placeholder="100"
              />
            </Field>
            <Field label="Valor del descuento ($)">
              <Input
                type="number"
                value={form.discountValue}
                onChange={(e) => setForm((f) => ({ ...f, discountValue: e.target.value }))}
                placeholder="25.00"
              />
            </Field>
          </div>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))}
                className="rounded"
              />
              Activa
            </label>
            <div className="flex gap-2 ml-auto">
              <Button onClick={cancelForm} variant="secondary">Cancelar</Button>
              <Button
                onClick={() => saveMutation.mutate()}
                loading={saveMutation.isPending}
                disabled={!form.label || !form.pointsCost || !form.discountValue}
              >
                {editId ? 'Guardar cambios' : 'Crear recompensa'}
              </Button>
            </div>
          </div>
          {saveMutation.isError && (
            <p className="text-sm text-red-500">{(saveMutation.error as Error).message}</p>
          )}
        </div>
      )}

      <div className="divide-y divide-gray-50">
        {isLoading && <p className="px-6 py-4 text-sm text-gray-400">Cargando…</p>}
        {!isLoading && rewards.length === 0 && (
          <p className="px-6 py-6 text-sm text-gray-400 text-center">
            Sin recompensas configuradas. Agrega la primera.
          </p>
        )}
        {rewards.map((r) => (
          <div key={r.id} className="px-6 py-4 flex items-center gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900">{r.label}</span>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                  r.active ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                }`}>
                  {r.active ? 'Activa' : 'Inactiva'}
                </span>
              </div>
              <div className="flex gap-4 mt-1 text-sm text-gray-500">
                <span>⭐ {r.pointsCost} puntos</span>
                <span>→ ${Number(r.discountValue).toFixed(2)} de descuento</span>
              </div>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <button
                onClick={() => toggleMutation.mutate({ id: r.id, active: !r.active })}
                className="text-xs text-gray-500 hover:text-gray-800 underline"
              >
                {r.active ? 'Desactivar' : 'Activar'}
              </button>
              <button
                onClick={() => openEdit(r)}
                className="text-xs text-blue-600 hover:text-blue-800 underline"
              >
                Editar
              </button>
              <button
                onClick={() => {
                  if (confirm('¿Eliminar esta recompensa?')) deleteMutation.mutate(r.id)
                }}
                className="text-xs text-red-500 hover:text-red-700 underline"
              >
                Eliminar
              </button>
            </div>
          </div>
        ))}
      </div>
    </Card>
  )
}

// ─── Cuentas de clientes ──────────────────────────────────────────────────────
function AccountsSection() {
  const [search, setSearch] = useState('')

  const { data: accounts = [], isLoading } = useQuery<LoyaltyAccount[]>({
    queryKey: ['loyalty-accounts'],
    queryFn: async () => (await api.get('/loyalty/accounts')).data,
  })

  const filtered = accounts.filter((a) => a.phone.includes(search.trim()))

  return (
    <Card>
      <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-4">
        <h2 className="font-bold text-gray-900 flex-1">Cuentas de clientes</h2>
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por teléfono…"
          className="max-w-xs"
        />
      </div>
      <div className="divide-y divide-gray-50">
        {isLoading && <p className="px-6 py-4 text-sm text-gray-400">Cargando…</p>}
        {!isLoading && filtered.length === 0 && (
          <p className="px-6 py-6 text-sm text-gray-400 text-center">
            {search ? 'Sin resultados.' : 'Aún no hay clientes registrados.'}
          </p>
        )}
        {filtered.map((a) => (
          <div key={a.id} className="px-6 py-4 flex items-center">
            <div className="flex-1">
              <p className="font-semibold text-gray-900">{a.phone}</p>
              <p className="text-xs text-gray-400 mt-0.5">
                {a._count.transactions} transacción(es) · Desde{' '}
                {new Date(a.createdAt).toLocaleDateString('es-MX')}
              </p>
            </div>
            <p className="text-lg font-bold text-gray-900">{a.points} pts</p>
          </div>
        ))}
      </div>
    </Card>
  )
}
